import React from "react";
import { Flame, Clock } from "lucide-react";

// A ring, not a chart: one ratio against a limit is a meter.
const GoalRing = ({ percent, tone }) => {
  const size = 76;
  const stroke = 7;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = circumference * Math.min(1, percent / 100);

  return (
    <svg width={size} height={size} className="flex-shrink-0" role="img" aria-label={`${Math.round(percent)}% of today's goal`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="hsl(var(--muted))"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={tone}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${filled} ${circumference}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dasharray 700ms cubic-bezier(0.4, 0, 0.2, 1)" }}
      />
    </svg>
  );
};

const TodayFocus = ({ today }) => {
  if (!today) return null;

  const { minutes = 0, goalMinutes = 30, streak = 0, streakAtRisk, minutesToKeepStreak = 0 } = today;
  const percent = goalMinutes ? (minutes / goalMinutes) * 100 : 0;
  const goalMet = minutes >= goalMinutes;

  // The ring only turns amber when there is something real to lose today.
  const tone = streakAtRisk && streak > 0 ? "hsl(38 92% 50%)" : "hsl(var(--primary))";

  const headline = goalMet
    ? "Today's goal is done"
    : minutes > 0
      ? `${goalMinutes - minutes} min to today's goal`
      : "Nothing studied yet today";

  const sub = streakAtRisk && streak > 0
    ? `${minutesToKeepStreak} more minute${minutesToKeepStreak === 1 ? "" : "s"} keeps your ${streak}-day streak alive.`
    : streak > 0
      ? `${streak}-day streak — today already counts.`
      : "Five minutes today starts a streak.";

  return (
    <div className="bg-card rounded-2xl border border-border/40 p-5 shadow-sm mb-6">
      <div className="flex items-center gap-5">
        <div className="relative">
          <GoalRing percent={percent} tone={tone} />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-lg font-black text-foreground leading-none">{minutes}</span>
            <span className="text-[9px] text-muted-foreground font-bold uppercase tracking-wide mt-0.5">min</span>
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-foreground">{headline}</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{sub}</p>

          <div className="flex items-center gap-4 mt-3">
            <span className="flex items-center gap-1.5 text-[11px] font-bold text-muted-foreground">
              <Clock className="w-3.5 h-3.5" /> Goal {goalMinutes} min
            </span>
            {streak > 0 && (
              <span
                className={`flex items-center gap-1.5 text-[11px] font-bold ${
                  streakAtRisk ? "text-amber-600" : "text-muted-foreground"
                }`}
              >
                <Flame className="w-3.5 h-3.5" /> {streak} day{streak === 1 ? "" : "s"}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodayFocus;
